/**
 * 게임 세션 시작 오케스트레이션 — 결정 #25/#28. 선택한 게임 모듈로 새 세션(Chat)을
 * 만든다. 모듈마다 진행자(게임마스터/내레이터/안내자) 캐릭터를 시드하고, 모듈의
 * initialState로 moduleState를 채우며, 첫 장면을 여는 인사말 메시지를 넣는다.
 *
 * ChatsView의 "새 대화 → 모듈 선택"과 GamesView의 "플레이"가 공유한다(중복 제거).
 * storage 포트만 다룬다(결정 #10). 생성된 chatId를 반환해 호출부가 화면을 연다.
 */
import {
  createCharacter,
  createChat,
  createMessage,
  ROLEPLAY_MODULE_ID,
  type GameModule,
} from "@roqeet/core";
import { storage } from "./storage.js";

/**
 * 모듈로 새 세션을 만들고 생성된 chatId를 반환한다(결정 #25/#28). 캐릭터·세션·인사말
 * 메시지를 저장한다. moduleState는 모듈의 initialState로 시드된다(구조화 게임).
 */
export async function startGameSession(module: GameModule, t?: (k: string, ...args: any[]) => string): Promise<string> {
  const info = characterInitFor(module, t);
  const character = createCharacter(info);
  await storage.characters.put(character);

  const chat = createChat({
    title: titleFor(module, t),
    characterId: character.id,
    moduleId: module.manifest.id,
    ...(module.initialState ? { moduleState: module.initialState(undefined, t) } : {}),
  });
  chat.lastMessageAt = Date.now();
  chat.messageCount = 1;
  await storage.chats.put(chat);

  const greeting = createMessage({
    chatId: chat.id,
    senderType: "character",
    characterId: character.id,
    senderName: character.name,
    content: character.firstMessage,
    order: 0,
    isGreeting: true,
  });
  await storage.putMessage(greeting);

  return chat.id;
}

/**
 * 종료된 게임 세션을 같은 대화에서 다시 시작한다(결정 #28). 모듈 상태를 initialState로
 * 리셋하고 새 인사말 메시지를 끝에 덧붙인다. 이전 트랜스크립트는 보존되므로(결정 #9)
 * 플레이어가 지난 플레이를 스크롤로 되돌아볼 수 있다. 진행자 캐릭터는 그대로 재사용.
 */
export async function restartGameSession(chatId: string, t?: (k: string, ...args: any[]) => string): Promise<void> {
  const chat = await storage.chats.get(chatId);
  if (!chat) throw new Error(t ? t("gameEd.chatNotFound", "대화를 찾을 수 없습니다.") : "대화를 찾을 수 없습니다.");

  const { gameModules } = await import("./game-modules.js");
  const module = gameModules.get(chat.moduleId ?? ROLEPLAY_MODULE_ID);

  // 진행자 캐릭터를 찾아 새 인사말 작성자로 쓴다(없으면 인사말 생략).
  const seat = chat.participants.find((p) => p.type === "character");
  const character = seat?.characterId ? await storage.characters.get(seat.characterId) : undefined;

  // 현재 메시지 끝 order 다음에 새 인사말을 붙인다(트랜스크립트 보존, 결정 #9).
  const messages = await readMessagesOnce(chatId);
  const nextOrder = messages.length ? Math.max(...messages.map((m) => m.order)) + 1 : 0;

  if (character) {
    const info = characterInitFor(module ?? { manifest: { id: chat.moduleId ?? "", title: "", kind: "native" } }, t);
    const greeting = createMessage({
      chatId,
      senderType: "character",
      characterId: character.id,
      senderName: character.name,
      content: info.firstMessage,
      order: nextOrder,
      isGreeting: true,
    });
    await storage.putMessage(greeting);
  }

  // 모듈 상태를 초기값으로 리셋(구조화 게임). 자유대화는 moduleState가 없어 그대로.
  await storage.chats.put({
    ...chat,
    ...(module?.initialState ? { moduleState: module.initialState(undefined, t) } : { moduleState: {} }),
    lastMessageAt: Date.now(),
    messageCount: (chat.messageCount ?? messages.length) + (character ? 1 : 0),
  });
}

/** liveMessages를 1회 읽어 현재 메시지 배열을 얻는다(재시작 order 계산용). */
async function readMessagesOnce(chatId: string): Promise<import("@roqeet/core").Message[]> {
  const live = storage.liveMessages(chatId);
  return new Promise((resolve) => {
    const unsub = live.subscribe((v) => {
      resolve(v);
      queueMicrotask(unsub);
    });
  });
}

/** 새 세션 제목(결정 #28). 롤플레잉은 기존 표기 유지, 게임은 모듈명 기반. */
export function titleFor(module: GameModule, t?: (k: string, ...args: any[]) => string): string {
  if (module.manifest.id === ROLEPLAY_MODULE_ID) {
    return translateSession(t, "game.session.roleplayTitle", "안내자와의 대화");
  }
  const title = module.manifest.titleKey
    ? translateSession(t, module.manifest.titleKey, module.manifest.title)
    : module.manifest.title;
  return translateSession(t, "game.session.playTitle", "{title} 플레이", { title });
}

/**
 * 모듈별 시드 캐릭터 정보(결정 #25/#26). 롤플레잉은 자유대화 안내자, 빌트인 구조화
 * 게임은 장르에 맞는 진행자와 첫 장면 인트로를 만든다. 데이터형 카드(UUID id)는
 * default로 떨어져 모듈 제목 기반의 일반 진행자가 된다. firstMessage는 모델 응답을
 * 기다리지 않고 첫 장면을 여는 인사말(결정 #16 greeting).
 */
export function characterInitFor(module: GameModule, t?: (k: string, ...args: any[]) => string): {
  name: string;
  description: string;
  firstMessage: string;
} {
  const tf = (key: string, fallback: string, params?: Record<string, unknown>) =>
    translateSession(t, key, fallback, params);
  
  switch (module.manifest.id) {
    case ROLEPLAY_MODULE_ID:
      return {
        name: tf("game.session.guideName", "안내자"),
        description: tf("game.session.guideDesc", "롤플레잉을 도와주는 기본 캐릭터."),
        firstMessage: tf("game.session.guideFirstMsg", "안녕하세요. 무엇을 함께 그려볼까요?"),
      };
    case "text-adventure":
      return {
        name: tf("game.session.gmName", "게임마스터"),
        description: tf("game.session.taDesc", "텍스트 어드벤처를 진행하는 AI 게임마스터."),
        firstMessage: tf("game.session.taFirstMsg", "모험이 시작됩니다. 당신은 낯선 길의 입구에 서 있습니다. 무엇을 하시겠어요?"),
      };
    case "stat-rpg":
      return {
        name: tf("game.session.gmName", "게임마스터"),
        description: tf("game.session.srDesc", "스탯 RPG를 진행하는 AI 게임마스터. 주사위 판정으로 결과를 정한다."),
        firstMessage: tf("game.session.srFirstMsg", "여정이 시작됩니다. 당신은 갓 모험가가 된 신참입니다. 눈앞에 길이 갈라집니다 — 어디로 향하시겠어요?"),
      };
    case "visual-novel":
      return {
        name: tf("game.session.vnName", "내레이터"),
        description: tf("game.session.vnDesc", "비주얼노벨을 이끄는 내레이터이자 등장인물들."),
        firstMessage: tf("game.session.vnFirstMsg", "새 학기 첫날, 낯선 듯 설레는 공기 속에서 이야기가 시작됩니다. 당신은 어떤 하루를 보내고 싶나요?"),
      };
    case "escape-room":
      return {
        name: tf("game.session.erName", "진행자"),
        description: tf("game.session.erDesc", "방탈출을 진행하는 AI. 단서를 모아 잠긴 문을 열어야 한다."),
        firstMessage: tf("game.session.erFirstMsg", "정신을 차려 보니 창문 없는 어두운 방입니다. 문은 굳게 잠겨 있고, 어딘가에 탈출의 단서가 숨어 있습니다. 무엇부터 살펴보시겠어요?"),
      };
    case "social-deduction":
      return {
        name: tf("game.session.sdName", "진행자"),
        description: tf("game.session.sdDesc", "소셜 추리 게임을 진행하는 AI. 마을에 숨은 범인을 투표로 찾는다."),
        firstMessage: tf("game.session.sdFirstMsg", "평화롭던 마을에 사건이 벌어졌습니다. 용의자들이 한자리에 모였고, 그중 하나가 범인입니다. 잠시 후 인물들을 소개할게요. 시작할 준비가 되면 말씀하세요."),
      };
    default: {
      const title = module.manifest.titleKey ? tf(module.manifest.titleKey, module.manifest.title) : module.manifest.title;
      return {
        name: tf("game.session.gmName", "게임마스터"),
        description: tf("game.session.defaultDesc", "{title} 게임을 진행하는 AI 게임마스터.", { title }),
        firstMessage: tf("game.session.defaultFirstMsg", "{title}을(를) 시작합니다. 준비되면 입력해 주세요.", { title }),
      };
    }
  }
}

function translateSession(
  t: ((k: string, ...args: unknown[]) => string) | undefined,
  key: string,
  fallback: string,
  params?: Record<string, unknown>,
): string {
  if (!t) return interpolateFallback(fallback, params);

  const appStyle = t(key, params ?? {});
  if (appStyle && appStyle !== key) return appStyle;

  const fallbackStyle = t(key, fallback, params);
  if (fallbackStyle && fallbackStyle !== key) return fallbackStyle;

  return interpolateFallback(fallback, params);
}

function interpolateFallback(template: string, params?: Record<string, unknown>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_match, key: string) => String(params[key] ?? `{${key}}`));
}
